import React, { ChangeEvent } from 'react';
import { adminUpdateProductVariables, adminProduct_adminProducts_data_attributes, adminProduct_adminProducts_data, ProductAlternativeQuantityName } from '@api/graphql/types';
import Paths from '@root/utils/Path';
import { compose } from 'react-apollo';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { withRouter } from 'react-router';
import GraphQLClient from '@api/graphql/GraphQLClient';
import * as Mutations from '@api/graphql/mutations';
import ProductAttributeForm from './ProductAttributeForm';
import ProductInfo from './ProductInfo';
import ProductCategoryForm from './ProductCategoryForm';
import ProductImageForm from './ProductImageForm';
import Form from '@components/Form/Form';

interface Props {
    product: adminProduct_adminProducts_data;
}

type PropType = Props & InjectedIntlProps;

export type ProductFormData = adminUpdateProductVariables & {};


interface State {
    formData: ProductFormData;
}

class ProductEditForm extends React.Component<PropType, State> {

    public constructor(props: PropType) {
        super(props);
        this.state = {
            formData: this.getFormData(),
        };
    }

    private getAttributeOptionIds(attributes: adminProduct_adminProducts_data_attributes[] | null): string[] {
        const result: string[] = [];
        if (!attributes) {
            return result;
        }
        attributes.forEach((attribute) => {
            if (attribute.options) {
                const optionIds = attribute.options!.map((option) => option.id);
                result.push(...optionIds);
            }
        });

        return result;
    }

    private getCategoryIds(): string[] {
        const { product } = this.props;
        const defaultString: string | null = product.default_category_id ? product.default_category_id.toString() : null;
        const categoryIds: string[] = product.categories ? product.categories.map((category) => category.id).filter((categoryId) => categoryId !== defaultString) : [];
        return defaultString ? [defaultString, ...categoryIds] : categoryIds;
    }

    private getFormData = (): ProductFormData => {
        const { product } = this.props;
        return {
            id: product.id,
            status: product.status,
            gift_message_available: product.gift_message_available,
            is_mpl: product.is_mpl,
            description: product.description,
            components: product.components,
            nutrition_data: product.nutrition_data,
            meta_title: product.meta_title,
            meta_description: product.meta_description,
            meta_keywords: product.meta_keywords,
            min_expiry_days: product.min_expiry_days,
            sliceable: product.sliceable,
            weight: product.weight,
            attribute_option_ids: this.getAttributeOptionIds(product.attributes),
            category_ids: this.getCategoryIds(),
            alternative_quantity_name: product.alternative_quantity_name,
            alternative_quantity_scale: product.alternative_quantity_scale,
            alternative_quantity_minimum: product.alternative_quantity_minimum,
            alternative_quantity_exchange_rate: product.alternative_quantity_exchange_rate,
            default_quantity_scale: product.default_quantity_scale,
            default_quantity_minimum: product.default_quantity_minimum,
            is_qty_decimal: product.is_qty_decimal,
            webshop_only_price: product.webshop_only_price,
        };
    }

    public onSubmit = (variables: ProductFormData) => {
        return GraphQLClient.mutate({
            mutation: Mutations.adminUpdateProduct, variables,
        });
    }

    private onChange = (value: any, name: keyof ProductFormData) => {
        if (this.state.formData) {
            this.setState({ formData: { ...this.state.formData, [name]: value } });
        }
    }

    public onFormChange = (formData: ProductFormData) => {
        if (this.state.formData) {
            this.setState({ formData });
        }
    }

    private validate = (formData: ProductFormData): boolean => {
        return true;
    }

    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `module.product.${id}` });
    }

    public render() {
        const alternativeQuantityNameField = (
            <select
                className="form-control"
                name="alternative_quantity_name"
                defaultValue={(this.state.formData && this.state.formData.alternative_quantity_name) ? this.state.formData.alternative_quantity_name : undefined}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                    this.onChange(e.currentTarget.value, 'alternative_quantity_name');
                }}
            >
                <option key={'alternative_quantity_name-please_select'} value=''>{this.label(`alternative_quantity_name.please_select`)}</option>
                {Object.keys(ProductAlternativeQuantityName).map((name: ProductAlternativeQuantityName, index: number) => {
                    return <option key={`alternative_quantity_name-${index}`} value={name}>{name}</option>;
                })}
            </select>
        );

        const defaultQuantityNameField = (
            <input type="text" className="form-control" value={this.props.product.default_quantity_name || ''} readOnly={true} />
        );

        const fields = [
            {
                id: 'default',
                fields: [
                    { name: 'status', type: 'productstatusselect' }, //
                    { name: 'is_mpl', type: 'checkbox' }, //
                    { name: 'description', type: 'markdown', props: {prefix: 'products'} }, //
                    { name: 'components', type: 'markdown', props: {prefix: 'products'} }, //
                    { name: 'nutrition_data', type: 'markdown', props: {prefix: 'products'} }, //
                    { name: 'meta_title', type: 'text' }, //
                    { name: 'meta_description', type: 'textarea' }, //
                    { name: 'meta_keywords', type: 'text' }, //
                    { name: 'min_expiry_days', type: 'number' }, //
                    { name: 'weight', type: 'number' }, //
                    { name: 'sliceable', type: 'checkbox' }, //
                    { name: 'gift_message_available', type: 'checkbox' },
                    { name: 'webshop_only_price', type: 'checkbox' },

                    { name: 'default_quantity_name', element: defaultQuantityNameField, props: {before: <hr />} }, //
                    { name: 'default_quantity_scale', type: 'number' }, //
                    { name: 'default_quantity_minimum', type: 'number' }, //
                    { name: 'is_qty_decimal', type: 'checkbox' }, //

                    { name: 'alternative_quantity_name', element: alternativeQuantityNameField, props: {before: <hr />} },
                    { name: 'alternative_quantity_scale', type: 'number' }, //
                    { name: 'alternative_quantity_minimum', type: 'number' }, //
                    { name: 'alternative_quantity_exchange_rate', type: 'number' }, //
                ],
            },
            {
                id: 'attributes',
                fields: [
                    <ProductAttributeForm key="attributes" name="attribute_option_ids" value={this.state.formData.attribute_option_ids} onChange={this.onChange} />,
                ],
            },
            {
                id: 'categories',
                fields: [
                    <ProductCategoryForm key="categories" name="category_ids" value={this.state.formData.category_ids} onChange={this.onChange} />,
                ],
            },
            {
                id: 'images',
                fields: [
                    <ProductImageForm key="images" product={this.props.product} />,
                ],
            },
            {
                id: 'info',
                fields: [
                    <ProductInfo key="info" product={this.props.product} />,
                ],
            },
        ];


        return (
            <>
                <h2>{this.props.product.name}</h2>

                <Form
                    intlPrefix={`module.product`}
                    redirectPath={`${Paths.product}`}
                    fields={fields}
                    formData={this.state.formData}
                    onSubmit={this.onSubmit}
                    validate={this.validate}
                    onChange={this.onFormChange}
                />
            </>
        );
    }
}

export default compose(
    injectIntl,
    withRouter,
)(ProductEditForm);




// WEBPACK FOOTER //
// ./src/modules/Product/components/ProductEditForm.tsx